import {
  Box,
  Divider,
  Drawer,
  ListItemText,
  ListSubheader,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  DrawerContent,
  MENU_ITEM_TYPOGRAPHY_PROPS,
  StyledList,
  StyledListItem,
  StyledListSubheader,
} from '@src/components/common/menu/LinkMenuStyles';
import { type MenuItem, useMenuData } from '@src/components/common/menu/useMenuData';
import { FeatureFlag, useFeatureFlag } from '@src/config/featureFlags';
import {
  deleteAllStorageValue,
  setColorModeToStorage,
  setFocusModeToStorage,
} from '@src/modules/local_storage_utils';
import {
  type ColorMode,
  selectColorMode,
  selectFocusMode,
  updateColorMode,
  updateFocusMode,
} from '@src/store/slices/focus_mode';
import {
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

/**
 * LinkMenuコンポーネントのProps型定義
 */
type LinkMenuProps = {
  show: boolean;
  onClose: () => void;
};

/**
 * リンクメニューコンポーネント
 *
 * ナビゲーションメニューを提供するコンポーネントです。
 * 右側からスライドインするドロワーメニューとして表示されます。
 */
function LinkMenu(props: LinkMenuProps): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [state, setState] = useState<boolean>(false);
  const menuId = useId();
  const theme = useTheme();
  const focusToggleTimeoutRef = useRef<number | null>(null);
  const isFocusModeEnabled = useFeatureFlag(FeatureFlag.FOCUS_MODE);

  const isFocusMode = useSelector(selectFocusMode) || false;
  const colorMode = useSelector(selectColorMode);

  const { currentMainContent, otherMenuContent, currentPath } = useMenuData();

  /**
   * ドロワーの開閉処理
   */
  const toggleDrawer = useCallback(
    (open: boolean) =>
      (event: React.KeyboardEvent | React.MouseEvent): void => {
        if (
          event.type === 'keydown' &&
          ((event as React.KeyboardEvent).key === 'Tab' ||
            (event as React.KeyboardEvent).key === 'Shift')
        ) {
          return;
        }

        setState(open);
        props.onClose();
      },
    [props]
  );

  /**
   * メニューアイテムクリック時の処理
   */
  const handleMenuItemClick = useCallback(
    (item: MenuItem): void => {
      if (item.translationKey === 'menu.initializeInputContent') {
        deleteAllStorageValue();
        window.location.replace('/');
      } else if (item.label === 'FAQ') {
        navigate(item.path);
      } else {
        navigate(item.path);
      }
    },
    [navigate]
  );

  /**
   * フォーカスモード切り替え時の処理
   */
  const focusModeToggleDelay = useMemo(() => {
    const leavingDuration = theme.transitions.duration.leavingScreen;
    return typeof leavingDuration === 'number' ? Math.max(200, leavingDuration) : 220;
  }, [theme.transitions.duration.leavingScreen]);

  const handleFocusModeToggle = useCallback(() => {
    if (focusToggleTimeoutRef.current) {
      window.clearTimeout(focusToggleTimeoutRef.current);
    }

    const nextFocusMode = !isFocusMode;

    setState(false);
    props.onClose();

    focusToggleTimeoutRef.current = window.setTimeout(() => {
      dispatch(updateFocusMode(nextFocusMode));
      setFocusModeToStorage(nextFocusMode);
      focusToggleTimeoutRef.current = null;
    }, focusModeToggleDelay);
  }, [dispatch, focusModeToggleDelay, isFocusMode, props.onClose]);

  const handleColorModeChange = useCallback(
    (_event: React.MouseEvent<HTMLElement>, nextMode: ColorMode | null) => {
      if (!nextMode) {
        return;
      }
      dispatch(updateColorMode(nextMode));
      setColorModeToStorage(nextMode);
    },
    [dispatch]
  );

  /**
   * メニューリストのレンダリング
   */
  const renderMenuList = useCallback((): ReactElement => {
    const renderSection = (title: string, items: MenuItem[]): ReactNode => {
      if (!items.length) {
        return null;
      }
      return (
        <Box>
          <StyledListSubheader {...({ component: 'div' } as any)}>{title}</StyledListSubheader>
          <StyledList role="none">
            {items.map((item) => {
              const isActive =
                item.relatedPaths?.includes(currentPath) || item.path === currentPath;
              return (
                <StyledListItem
                  key={`${title}-${item.label}`}
                  role="menuitem"
                  onClick={() => handleMenuItemClick(item)}
                  selected={Boolean(isActive)}
                  aria-label={`${item.label} ${t('navigation.goToPage')}`}
                >
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      ...MENU_ITEM_TYPOGRAPHY_PROPS,
                      fontWeight: isActive ? 800 : MENU_ITEM_TYPOGRAPHY_PROPS.fontWeight,
                    }}
                  />
                </StyledListItem>
              );
            })}
          </StyledList>
        </Box>
      );
    };

    const mainSection = renderSection(t('resume.title'), currentMainContent);
    const utilitySection = renderSection(t('menu.otherContents'), otherMenuContent);

    return (
      <DrawerContent
        role="menu"
        id={menuId}
        tabIndex={0}
        onClick={toggleDrawer(false)}
        onKeyDown={toggleDrawer(false)}
        aria-label={t('navigation.mainNavigation')}
      >
        <Box>
          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={{ mb: 0.5, fontSize: { xs: '0.7rem', lg: '0.875rem' } }}
          >
            {t('navigation.mainNavigation')}
          </Typography>
          <Typography
            variant="h5"
            component="p"
            sx={{ fontWeight: 700, mb: 1, fontSize: { xs: '1.2rem', lg: '1.5rem' } }}
          >
            {t('resume.title')}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: { xs: '0.7rem', lg: '0.875rem' } }}
          >
            {t('navigation.resume')}
          </Typography>
        </Box>
        {mainSection}
        {mainSection && utilitySection ? <Divider flexItem /> : null}
        {utilitySection}
        <Box sx={{ mt: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box>
            <StyledListSubheader {...({ component: 'div' } as any)}>
              {t('menu.appearance')}
            </StyledListSubheader>
            <ToggleButtonGroup
              exclusive
              fullWidth
              value={colorMode}
              onChange={handleColorModeChange}
              aria-label={t('navigation.colorModeToggle')}
              sx={{
                '& .MuiToggleButton-root': {
                  flex: 1,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 700,
                  // デフォルトはデスクトップサイズ、モバイル時（lg未満）は20%小さく
                  fontSize: { xs: '0.7rem', lg: '0.875rem' },
                  padding: theme.spacing(1, 1.5),
                  borderColor: alpha(theme.palette.text.primary, 0.08),
                  '&.Mui-selected': {
                    color: theme.palette.primary.main,
                    backgroundColor: alpha(theme.palette.primary.main, 0.12),
                  },
                  '&:not(:first-of-type)': {
                    marginLeft: theme.spacing(1),
                  },
                },
              }}
            >
              <ToggleButton value="light">{t('menu.lightMode')}</ToggleButton>
              <ToggleButton value="dark">{t('menu.darkMode')}</ToggleButton>
              <ToggleButton value="system">{t('menu.systemMode')}</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          {isFocusModeEnabled && (
            <Box>
              <StyledListSubheader {...({ component: 'div' } as any)}>
                {isFocusMode ? t('menu.exitFocusMode') : t('menu.focusMode')}
              </StyledListSubheader>
              <StyledList>
                <StyledListItem
                  key="focus-mode-toggle"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleFocusModeToggle();
                  }}
                  selected={isFocusMode}
                >
                  <ListItemText
                    primary={isFocusMode ? t('menu.exitFocusMode') : t('menu.focusMode')}
                    primaryTypographyProps={{
                      ...MENU_ITEM_TYPOGRAPHY_PROPS,
                      fontWeight: isFocusMode ? 800 : MENU_ITEM_TYPOGRAPHY_PROPS.fontWeight,
                    }}
                  />
                  <Switch
                    edge="end"
                    color="primary"
                    checked={isFocusMode}
                    onClick={(event) => event.stopPropagation()}
                    onChange={(event) => {
                      event.stopPropagation();
                      handleFocusModeToggle();
                    }}
                    inputProps={{ 'aria-label': t('navigation.focusModeToggle') }}
                  />
                </StyledListItem>
              </StyledList>
            </Box>
          )}
        </Box>
      </DrawerContent>
    );
  }, [
    currentMainContent,
    currentPath,
    handleMenuItemClick,
    handleColorModeChange,
    handleFocusModeToggle,
    colorMode,
    isFocusMode,
    isFocusModeEnabled,
    menuId,
    otherMenuContent,
    theme,
    t,
    toggleDrawer,
  ]);

  /**
   * メニューリストのメモ化
   */
  const memoizedMenuList = useMemo((): ReactElement => {
    return renderMenuList();
  }, [renderMenuList]);

  useEffect((): void => {
    setState(props.show);
  }, [props.show]);

  useEffect(() => {
    return () => {
      if (focusToggleTimeoutRef.current) {
        window.clearTimeout(focusToggleTimeoutRef.current);
      }
    };
  }, []);

  return (
    <Drawer
      anchor={'left'}
      open={state}
      onClose={toggleDrawer(false)}
      ModalProps={{
        keepMounted: true,
        disableScrollLock: true,
        BackdropProps: {
          sx: {
            backgroundColor: 'rgba(3, 7, 18, 0.7)',
            backdropFilter: 'blur(4px)',
          },
        },
      }}
      sx={{
        zIndex: 10000, // Navigationより高いz-indexを設定
        '& .MuiDrawer-paper': {
          zIndex: 10000,
          width: { xs: 300, sm: 320 },
          borderTopRightRadius: 24,
          borderBottomRightRadius: 24,
          borderRight: 'none',
          boxShadow: (theme) =>
            theme.palette.mode === 'dark'
              ? '8px 0 24px rgba(0, 0, 0, 0.45)'
              : '12px 0 32px rgba(15, 23, 42, 0.12)',
        },
      }}
    >
      {memoizedMenuList}
    </Drawer>
  );
}

export default LinkMenu;
